import React, { useState, useEffect, useMemo } from 'react';
import Loading from '../../Common/Loading';
import { useNavigate } from "react-router-dom";
import CustomAlert from '../../Common/CustomAlert';

type ViewState = 'LIST' | 'MODIFY' | 'DELETE' | 'DETAILS' | 'LESSONS' | 'TREE';

const EL_ManageDIC: React.FC = () => {

    const API_BASE =
        process.env.REACT_APP_DOCUMENT_LINK;

    // --- State ---
    const [currentView, setCurrentView] = useState<ViewState>('LIST');
    const [selectedDic, setSelectedDic] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    // Data States
    const [dicList, setDicList] = useState<any[]>([]);
    const [modifyData, setModifyData] = useState<any>(null);
    const [deleteImpact, setDeleteImpact] = useState<any[]>([]);
    const [structureData, setStructureData] = useState<{ details: any[], tree: any[] }>({ details: [], tree: [] });
    const [lessonsData, setLessonsData] = useState<any[]>([]);
    const [alertInfo, setAlertInfo] = useState({
        show: false,
        message: ''
    });

    const navigate = useNavigate();

    document.body.style.backgroundColor = '#fff';
    // --- Effects ---
    useEffect(() => {
        if (currentView === 'LIST') fetchDicList();
    }, [currentView]);

    // --- API Calls ---
    const fetchDicList = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}api/dic/list`);
            const json = await res.json();
            setDicList(json.results || []);
        } catch (err) {
            setAlertInfo({
                show: true,
                message: "Failed to load DIC list"
            });
        }
        finally { setLoading(false); }
    };

    const requireSelection = (callback: () => void) => {
        if (!selectedDic) {
            setAlertInfo({
                show: true,
                message: "Please select a DIC file from the list first."
            });
            return;
        }
        callback();
    };

    const handleViewDetails = () => requireSelection(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}api/dic/structure?dic=${selectedDic}`);
            const json = await res.json();
            setStructureData(json);
            setCurrentView('DETAILS');
        } catch (err) {
            setAlertInfo({
                show: true,
                message: "Failed to load details"
            });
        }
        finally { setLoading(false); }
    });

    const handleViewLessons = () => requireSelection(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}api/dic/lessons?dic=${selectedDic}`);
            const json = await res.json();
            setLessonsData(json.lessons || []);
            setCurrentView('LESSONS');
        } catch (err) {
            setAlertInfo({
                show: true,
                message: "Failed to load lessons"
            });
        }
        finally { setLoading(false); }
    });

    const handleModify = () => requireSelection(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}api/dic/details_for_modify?dic=${selectedDic}`);
            const json = await res.json();
            if (json.error) throw new Error(json.error);

            // highest existing order + 1, used only when this DIC has no order yet
            const maxOrder = dicList.reduce(
                (max, r) => Math.max(max, Number(r.DicFileOrder) || 0), 0
            );
            const current = json.DicFileOrder;
            const isEmpty = current === null || current === undefined || String(current).trim() === '';

            setModifyData({ ...json, DicFileOrder: isEmpty ? maxOrder + 1 : current });
            setCurrentView('MODIFY');
        } catch (err) {
            setAlertInfo({ show: true, message: "Failed to load modify details" });
        }
        finally { setLoading(false); }
    });

    const handleDeleteInit = () => requireSelection(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}api/dic/delete_impact?dic=${selectedDic}`);
            const json = await res.json();
            setDeleteImpact(json.impact || []);
            setCurrentView('DELETE');
        } catch (err) {
            setAlertInfo({
                show: true,
                message: "Failed to load delete impact"
            });
        }
        finally { setLoading(false); }
    });

    const executeDelete = async () => {
        setLoading(true);
        try {
            await fetch(`${API_BASE}api/dic/delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dic_name: selectedDic })
            });
            setAlertInfo({
                show: true,
                message: 'DIC Deleted Successfully'
            });
            setSelectedDic('');
            setCurrentView('LIST');
        } catch (err) {
            setAlertInfo({
                show: true,
                message: "Deletion failed"
            });
        }
        finally { setLoading(false); }
    };

    const saveModification = async () => {
        if (!modifyData.ShortDesc) {
            setAlertInfo({ show: true, message: "Short Description cannot be blank" });
            return;
        }
        const order = String(modifyData.DicFileOrder ?? '').trim();
        if (order === '') {
            setAlertInfo({ show: true, message: "DIC File Order is required. Please enter a number." });
            return;
        }
        if (!/^\d+$/.test(order) || Number(order) < 1) {
            setAlertInfo({ show: true, message: "DIC File Order must be a whole number greater than 0." });
            return;
        }
        setLoading(true);
        try {
            await fetch(`${API_BASE}api/dic/save_modify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    dic_name: selectedDic,
                    short_desc: modifyData.ShortDesc,
                    long_desc: modifyData.LongDesc,
                    dic_file_order: modifyData.DicFileOrder,
                    hide_lessons: modifyData.HideLessonsInDic,
                    used_in: modifyData.UsedIn
                })
            });
            setAlertInfo({
                show: true,
                message: "Modifications saved successfully"
            });
            setCurrentView('LIST');
        } catch (err) {
            setAlertInfo({
                show: true,
                message: "Save failed"
            });
        }
        finally { setLoading(false); }
    };

    const handleSetOrder = () => window.location.href = "/WebAdmin/DIC/EL_WriteDicToDB";
    const handleAddDic = () => window.location.href = "/WebAdmin/DIC/EL_WriteDicToDB";

    // --- Modify Grid Checkbox Handler ---
    const toggleUsedIn = (country: string, year: number) => {
        const key = `${country}_${year}`;
        const newUsedIn = modifyData.UsedIn.includes(key)
            ? modifyData.UsedIn.filter((k: string) => k !== key)
            : [...modifyData.UsedIn, key];
        setModifyData({ ...modifyData, UsedIn: newUsedIn });
    };

    // --- Helper: Flatten Tree Data for Table Rendering ---
    const flattenedTreeData = useMemo(() => {
        if (!structureData.tree || structureData.tree.length === 0) return [];
        const flatList: { type: 'unit' | 'chapter' | 'activity', text: string }[] = [];
        let prevUnit = "";
        let prevChapter = "";

        structureData.tree.forEach(row => {
            if (row.Unit !== prevUnit) {
                flatList.push({ type: 'unit', text: row.Unit });
                prevUnit = row.Unit;
                prevChapter = ""; // Reset chapter on new unit
            }
            if (row.Chapter !== prevChapter) {
                flatList.push({ type: 'chapter', text: row.Chapter });
                prevChapter = row.Chapter;
            }
            flatList.push({ type: 'activity', text: row.Activity });
        });
        return flatList;
    }, [structureData.tree]);

    // --- Helper: Sort Countries in Desired Order ---
    const sortedCountries = useMemo(() => {
        if (!modifyData || !modifyData.AllCountries) return [];

        const desiredOrder = [
            'Australia',
            'USA',
            'Malaysia',
            'UK',
            'Canada',
            'Alberta',
            'Ontario',
            'AUSVELS',
            'CALIFORNIA',
            'NZ',
            'Philippines',
            'International',
            'INDIA',
            'SINGAPORE'
        ];

        const sorted = [...modifyData.AllCountries].sort((a: string, b: string) => {
            const indexA = desiredOrder.indexOf(a);
            const indexB = desiredOrder.indexOf(b);

            // If both are in the desired order, sort by position
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;

            // If only a is in the desired order, it comes first
            if (indexA !== -1) return -1;

            // If only b is in the desired order, it comes first
            if (indexB !== -1) return 1;

            // If neither is in the desired order, sort alphabetically
            return a.localeCompare(b);
        });

        return sorted;
    }, [modifyData]);


    // --- Exact Styles from Screenshots ---
    const styles = {
        // Top Action Buttons (Olive Green)
        btnOlive: {
            backgroundColor: '#808000',
            color: 'white',
            fontWeight: 'bold',
            width: '120px',
            padding: '3px',
            cursor: 'pointer',
            border: '1px solid black',
            margin: '0 5px',
            fontFamily: 'Arial, sans-serif',
            fontSize: '12px'
        },
        // Standard gray button for modify view
        btnStandard: {
            backgroundColor: '#e0e0e0',
            color: 'black',
            padding: '3px 15px',
            cursor: 'pointer',
            border: '1px solid #707070',
            marginRight: '10px',
            fontFamily: 'Arial, sans-serif',
            fontSize: '12px'
        },
        // Titles (Red, Underlined)
        titleText: {
            color: '#cc0000',
            textDecoration: 'underline',
            fontWeight: 'bold',
            fontSize: '16px',
            fontFamily: 'Times New Roman, serif'
        },
        // Base Table
        table: {
            width: '100%',
            borderCollapse: 'collapse' as const,
            fontFamily: 'Arial, sans-serif',
            fontSize: '12px',
            border: '1px solid #85aafa',
            overflow: "auto"
        },
        // List View & Tree View specific headers (Light blue with dark blue text)
        listTh: {
            backgroundColor: '#b8d1f3',
            color: '#003399',
            padding: '8px',
            textAlign: 'left' as const,
            borderBottom: '1px solid #6b97f7',
            borderRight: '1px solid #6b97f7',
            fontWeight: 'normal',
            fontSize: "10pt"
        },
        // Details & Lessons View specific headers (Maroon with white text)
        maroonTh: {
            backgroundColor: '#800000',
            color: 'white',
            padding: '7px',
            textAlign: 'left' as const,
            border: '1px solid #6b97f7',
            fontWeight: 'normal',
            fontSize: "10pt"
        },
        td: {
            padding: '10px 6px',
            borderRight: '1px solid #a3c0e8',
            cursor: 'pointer',
            fontSize: '10pt  '
        },
        selectedRow: {
            backgroundColor: '#FFA500',
            color: 'white',
            // fontWeight: 'bold'
        },
        altRow: { backgroundColor: '#F4FAFF' },
        defaultRow: { backgroundColor: 'white' },

        // Purple border input for Modify View
        purpleInput: {
            padding: '2px',
            border: '1px solid #6600FF',
            // fontFamily: 'sans-serif',
            fontSize: '10pt',
        }
    };

    const renderTitle = () => {
        let title = "List of Dic Files";
        if (currentView === 'DETAILS' || currentView === 'TREE') title = `Details of Dic File : ${selectedDic}`;
        if (currentView === 'LESSONS') title = `List of Lessons in Dic File : ${selectedDic}`;
        if (currentView === 'MODIFY') title = `Modifying Dic File for DIC : ${selectedDic}`;
        if (currentView === 'DELETE') title = `Deleting Dic File : ${selectedDic}`;
        return title;
    };

    return (


        <div style={{ fontFamily: 'Arial, sans-serif', margin: '10px' }}>
            {loading && <Loading />}



            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    onClose={() =>
                        setAlertInfo({
                            ...alertInfo,
                            show: false
                        })
                    }
                />
            )}
            {/* Menu Buttons */}
            {currentView === 'LIST' && (
                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                    <button style={styles.btnOlive} onClick={handleViewDetails}>Details</button>
                    <button style={styles.btnOlive} onClick={handleViewLessons}>Lessons</button>
                    <button style={styles.btnOlive} onClick={handleModify}>Modify</button>
                    <button style={styles.btnOlive} onClick={handleDeleteInit}>Delete</button>
                    <button style={styles.btnOlive} onClick={handleSetOrder}>Set Order</button>
                    <button style={styles.btnOlive} onClick={handleAddDic}>Add/Modify DIC</button>
                    <hr style={{ margin: '15px 0' }} />
                </div>
            )}

            {/* Title Bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ width: '20%' }}>
                    {(currentView === 'DETAILS' || currentView === 'TREE') && (
                        <span style={{ fontSize: '12px' }}>View:
                            <select value={currentView} onChange={(e) => setCurrentView(e.target.value as ViewState)} style={{ marginLeft: '5px' }}>
                                <option value="DETAILS">Table View</option>
                                <option value="TREE">Tree View</option>
                            </select>
                        </span>
                    )}
                </div>
                <div style={{ width: '60%', textAlign: 'center' }}>
                    <span style={styles.titleText}>{renderTitle()}</span>
                </div>
                <div style={{ width: '20%', textAlign: 'right' }}>
                    <button
                        style={styles.btnOlive}
                        onClick={() => {
                            if (currentView === 'LIST') {
                                navigate("/SysAdmin");
                            } else {
                                setCurrentView('LIST');
                            }
                        }}
                    >
                        Back
                    </button>

                </div>
            </div>

            {/* VIEWS */}
            {/* <div style={{ overflow: 'auto', maxHeight: '85vh', borderBottom: '1px solid #0000CC' }}> */}

            {/* 1. LIST VIEW */}
            {currentView === 'LIST' && (
                <div
                    style={{
                        overflow: 'auto',
                        maxHeight: '85vh',
                        width: '100%',
                        border: '1px solid #85aafa'
                    }}
                >
                    <table
                        style={{
                            ...styles.table,
                            tableLayout: 'auto',
                            minWidth: '100%',
                            width: 'max-content'
                        }}
                    >
                        <thead>
                            <tr>
                                <th style={{ ...styles.listTh, position: 'sticky', top: -2, zIndex: 2, whiteSpace: "nowrap" }}>#</th>
                                <th style={{ ...styles.listTh, position: 'sticky', top: -2, zIndex: 2, whiteSpace: "nowrap" }}>Dic File Name</th>
                                <th style={{ ...styles.listTh, position: 'sticky', top: -2, zIndex: 2, whiteSpace: "nowrap" }}>Short Desc</th>
                                <th style={{ ...styles.listTh, position: 'sticky', top: -2, zIndex: 2, whiteSpace: "nowrap" }}>Dic File<br />Order</th>
                                <th style={{ ...styles.listTh, position: 'sticky', top: -2, zIndex: 2, whiteSpace: "nowrap" }}>Subject</th>
                                <th style={{ ...styles.listTh, position: 'sticky', top: -2, zIndex: 2, width: "100%" }}>Used in<br />Curriculum: Years</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dicList.map((row, idx) => {
                                const isSelected = selectedDic === row.DicFileName;
                                return (
                                    <tr
                                        key={row.DicFileName}
                                        onClick={() => setSelectedDic(row.DicFileName)}
                                        style={isSelected ? styles.selectedRow : (idx % 2 === 0 ? styles.defaultRow : styles.altRow)}
                                    >
                                        <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{idx + 1}</td>
                                        <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{row.DicFileName}</td>
                                        <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{row.ShortDesc}</td>
                                        <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{row.DicFileOrder}</td>
                                        <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{row.Subject}</td>
                                        <td style={{ ...styles.td, width: "100%" }}>
                                            {row.Years.split('|').map((item: string, index: number) => (
                                                <React.Fragment key={index}>
                                                    {item.trim()}
                                                    {index < row.Years.split('|').length - 1 && <br />}
                                                </React.Fragment>
                                            ))}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* 2. DETAILS VIEW (Maroon Headers) */}
            {currentView === 'DETAILS' && (
                <div
                    style={{
                        overflow: 'auto',
                        maxHeight: '92vh',
                        width: '100%',
                        border: '1px solid #85aafa'
                    }}
                >
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th colSpan={10} style={{ ...styles.maroonTh, textAlign: 'center', position: 'sticky', top: 1, zIndex: 2 }}>
                                    List of records available for DIC : {selectedDic}
                                </th>
                            </tr>
                            <tr>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 20, zIndex: 2 }}>#</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 20, zIndex: 2, width: 'auto' }}>Activity</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 20, zIndex: 2 }}>LineNum</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 20, zIndex: 2 }}>LevelNo</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 20, zIndex: 2 }}>ParentCode</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 20, zIndex: 2 }}>ChildCode</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 20, zIndex: 2 }}>FileID</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 20, zIndex: 2 }}>Activity Name</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 20, zIndex: 2 }}>DepLessons</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 20, zIndex: 2 }}>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {structureData.details.map((row, idx) => (
                                <tr key={idx} style={idx % 2 === 0 ? styles.defaultRow : styles.altRow}>
                                    <td style={styles.td}>{idx + 1}</td>
                                    <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{row.Activity}</td>
                                    <td style={styles.td}>{row.LineNum}</td>
                                    <td style={styles.td}>{row.LevelNo}</td>
                                    <td style={styles.td}>{row.ParentCode}</td>
                                    <td style={styles.td}>{row.ChildCode}</td>
                                    <td style={styles.td}>{row.FileID}</td>
                                    <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{row.ActivityName}</td>
                                    <td style={styles.td}>{row.DepLessons}</td>
                                    <td style={styles.td}>{row.Remarks}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* 3. TREE VIEW (Flattened array mapping) */}
            {currentView === 'TREE' && (
                <div style={{ overflowX: 'auto', border: '1px solid #a0a0a0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Courier New, monospace', fontSize: '14px' }}>
                        <thead>
                            <tr>
                                <th style={styles.listTh}>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {flattenedTreeData.map((item, idx) => (
                                <tr key={idx} style={idx % 2 === 0 ? styles.defaultRow : styles.altRow}>
                                    <td
                                        style={{
                                            padding: '8px 6px',
                                            paddingLeft:
                                                item.type === 'unit'
                                                    ? '0px'
                                                    : item.type === 'chapter'
                                                        ? '30px'
                                                        : '60px',
                                            color:
                                                item.type === 'unit'
                                                    ? 'blue'
                                                    : item.type === 'chapter'
                                                        ? 'darkred'
                                                        : 'black',
                                            fontWeight: item.type === 'activity' ? 'normal' : 'bold',
                                            fontSize: '14pt',
                                        }}
                                    >
                                        {item.text}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* 4. DELETE VIEW */}
            {currentView === 'DELETE' && (
                <div style={{ textAlign: 'center' }}>
                    <table style={{ ...styles.table, width: '60%', margin: '0 auto', marginBottom: '20px' }}>
                        <thead>
                            <tr>
                                <th style={styles.maroonTh}>Table Name</th>
                                <th style={styles.maroonTh}>Description</th>
                                <th style={styles.maroonTh}>Record Count</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deleteImpact.map((row, idx) => (
                                <tr key={idx}>
                                    <td style={styles.td}>{row.TableName}</td>
                                    <td style={styles.td}>{row.TableDesc}</td>
                                    <td style={styles.td}>{row.RecCount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <br /><b><u>Confirm to Delete above Product records permanently from Database</u></b><br /><br />
                    <button style={{ backgroundColor: 'Red', color: 'White', fontWeight: 'bold', padding: '10px 20px', cursor: 'pointer', border: 'none', marginRight: '20px' }} onClick={executeDelete}>OK to Delete</button>
                    <button style={{ backgroundColor: 'Green', color: 'White', fontWeight: 'bold', padding: '10px 20px', cursor: 'pointer', border: 'none' }} onClick={() => setCurrentView('LIST')}>Cancel</button>
                </div>
            )}

            {/* 5. MODIFY VIEW */}
            {currentView === 'MODIFY' && modifyData && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, sans-serif', fontSize: '12pt' }}>
                    <tbody style={{ border: "2px solid black" }}>
                        <tr>
                            <td style={{ width: '10%', padding: '5px', verticalAlign: 'middle' }}>Short Description</td>
                            <td style={{ padding: '5px', verticalAlign: 'middle' }}>
                                <input style={{ ...styles.purpleInput, width: '180px' }} value={modifyData.ShortDesc} onChange={e => setModifyData({ ...modifyData, ShortDesc: e.target.value })} />
                                <span style={{ marginLeft: '25px', marginRight: '5px' }}>Dic File Order:</span>
                                <input style={{ ...styles.purpleInput, width: '100px' }} value={modifyData.DicFileOrder} onChange={e => setModifyData({ ...modifyData, DicFileOrder: e.target.value })} />
                                <span style={{ marginLeft: '25px', marginRight: '5px' }}>Subject:</span>
                                <input style={{ ...styles.purpleInput, width: '150px', backgroundColor: '#ebebe4' }} value={modifyData.Subject || ''} disabled />
                                <span style={{ marginLeft: '25px', marginRight: '5px', whiteSpace: 'nowrap' }}>Hide Lessons in DIC:</span>
                                <input type="checkbox" checked={modifyData.HideLessonsInDic} onChange={e => setModifyData({ ...modifyData, HideLessonsInDic: e.target.checked })} style={{ verticalAlign: 'middle' }} />
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: '5px', verticalAlign: 'top' }}>Used in</td>
                            <td style={{ padding: '5px' }}>
                                <table border={1} cellPadding={5} cellSpacing={0} style={{ fontFamily: 'Arial, sans-serif', borderCollapse: 'collapse', borderColor: 'black' }}>
                                    <thead>
                                        <tr style={{ color: 'maroon', fontWeight: 'bold' }}>
                                            <td style={{ padding: '5px 10px' }}>Curriculum</td>
                                            <td colSpan={13} style={{ textAlign: 'center', padding: '5px' }}>Years</td>
                                        </tr>
                                        <tr style={{ color: 'maroon', fontWeight: 'bold' }}>
                                            <td></td>
                                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(y => <td key={y} style={{ textAlign: 'center', width: '25px' }}>{y}</td>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedCountries.map((c: string) => (
                                            <tr key={c}>
                                                <td style={{ color: 'maroon', fontWeight: 'bold', padding: '5px 10px' }}>{c}</td>
                                                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(y => {
                                                    const isChecked = modifyData.UsedIn.includes(`${c}_${y}`);
                                                    return (
                                                        <td key={y} style={{ textAlign: 'center', cursor: 'pointer', verticalAlign: 'middle', height: '25px' }} onClick={() => toggleUsedIn(c, y)}>
                                                            {isChecked ? <span style={{ color: 'green', fontWeight: 'bold', fontSize: '14px' }}>✔</span> : null}
                                                        </td>
                                                    )
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: '5px', verticalAlign: 'top' }}>Long Description</td>
                            <td style={{ padding: '5px' }}>
                                <textarea style={{ ...styles.purpleInput, width: '100%', height: '60px', resize: 'vertical' }} value={modifyData.LongDesc} onChange={e => setModifyData({ ...modifyData, LongDesc: e.target.value })} />
                            </td>
                        </tr>
                        <tr>
                            <td></td>
                            <td style={{ padding: '10px 5px' }}>
                                <button style={styles.btnStandard} onClick={saveModification}>Save</button>
                                <button style={styles.btnStandard} onClick={() => setCurrentView('LIST')}>Cancel</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}

            {/* 6. LESSONS VIEW */}
            {/* {currentView === 'LESSONS' && (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th colSpan={29} style={{ ...styles.maroonTh, textAlign: 'center' }}>
                                        List of records available for DIC : {selectedDic}
                                    </th>
                                </tr>
                                <tr>
                                    <th style={styles.maroonTh}>#</th>
                                    <th style={styles.maroonTh}>Lesson</th>
                                    <th style={styles.maroonTh}>Ver</th>
                                    <th style={styles.maroonTh}>Qns</th>
                                    <th style={styles.maroonTh}>Tuts</th>
                                    <th style={styles.maroonTh}>E</th>
                                    <th style={styles.maroonTh}>M</th>
                                    <th style={styles.maroonTh}>D</th>
                                    <th style={styles.maroonTh}>Te</th>
                                    <th style={styles.maroonTh}>Tm</th>
                                    <th style={styles.maroonTh}>Td</th>
                                    <th style={styles.maroonTh}>Tt</th>
                                    <th style={styles.maroonTh}>Prcs</th>
                                    <th style={styles.maroonTh}>Clues</th>
                                    <th style={styles.maroonTh}>Sols</th>
                                    <th style={styles.maroonTh}>qB</th>
                                    <th style={styles.maroonTh}>qC</th>
                                    <th style={styles.maroonTh}>qD</th>
                                    <th style={styles.maroonTh}>qF</th>
                                    <th style={styles.maroonTh}>qI</th>
                                    <th style={styles.maroonTh}>qL</th>
                                    <th style={styles.maroonTh}>qM</th>
                                    <th style={styles.maroonTh}>qN</th>
                                    <th style={styles.maroonTh}>qO</th>
                                    <th style={styles.maroonTh}>qW</th>
                                    <th style={styles.maroonTh}>qZ</th>
                                    <th style={styles.maroonTh}>Dic Description</th>
                                    <th style={styles.maroonTh}>Lsn Description</th>
                                    <th style={styles.maroonTh}>Lesson2</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lessonsData.map((row, idx) => (
                                    <tr key={idx} style={idx % 2 === 0 ? styles.defaultRow : styles.altRow}>
                                        <td style={styles.td}>{idx + 1}</td>
                                        <td style={styles.td}>{row.Lesson}</td>
                                        <td style={styles.td}>{row.Ver}</td>
                                        <td style={styles.td}>{row.Qns || ''}</td>
                                        <td style={styles.td}>{row.Tuts || ''}</td>
                                        <td style={styles.td}>{row.E || ''}</td>
                                        <td style={styles.td}>{row.M || ''}</td>
                                        <td style={styles.td}>{row.D || ''}</td>
                                        <td style={styles.td}>{row.Te || ''}</td>
                                        <td style={styles.td}>{row.Tm || ''}</td>
                                        <td style={styles.td}>{row.Td || ''}</td>
                                        <td style={styles.td}>{row.Tt || ''}</td>
                                        <td style={styles.td}>{row.Prcs || ''}</td>
                                        <td style={styles.td}>{row.Clues || ''}</td>
                                        <td style={styles.td}>{row.Sols || ''}</td>
                                        <td style={styles.td}>{row.qB || ''}</td>
                                        <td style={styles.td}>{row.qC || ''}</td>
                                        <td style={styles.td}>{row.qD || ''}</td>
                                        <td style={styles.td}>{row.qF || ''}</td>
                                        <td style={styles.td}>{row.qI || ''}</td>
                                        <td style={styles.td}>{row.qL || ''}</td>
                                        <td style={styles.td}>{row.qM || ''}</td>
                                        <td style={styles.td}>{row.qN || ''}</td>
                                        <td style={styles.td}>{row.qO || ''}</td>
                                        <td style={styles.td}>{row.qW || ''}</td>
                                        <td style={styles.td}>{row.qZ || ''}</td>
                                        <td style={styles.td}>{row.DicDescription}</td>
                                        <td style={styles.td}>{row.LsnDescription}</td>
                                        <td style={styles.td}>{row.Lesson2}</td>
                                    </tr>

                                ))}
                                <tr style={{ backgroundColor: '#FFFFCC', fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>
                                    <td style={styles.td}></td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>Totals</td>
                                    <td style={styles.td}></td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Qns) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Tuts) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.E) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.M) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.D) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Te) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Tm) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Td) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Tt) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Prcs) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Clues) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Sols) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qB) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qC) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qD) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qF) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qI) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qL) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qM) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qN) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qO) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qW) || 0), 0) || ''}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qZ) || 0), 0) || ''}</td>
                                    <td style={styles.td}></td>
                                    <td style={styles.td}></td>
                                    <td style={styles.td}></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )} */}
            {currentView === 'LESSONS' && (
                <div
                    style={{
                        overflow: 'auto',
                        maxHeight: '92vh',
                        width: '100%',
                        border: '1px solid #85aafa'
                    }}
                >
                    <table style={{ ...styles.table, }}>
                        <thead>
                            <tr>
                                <th colSpan={29} style={{ ...styles.maroonTh, textAlign: 'center', position: 'sticky', top: 0, zIndex: 3 }}>
                                    List of records available for DIC : {selectedDic}
                                </th>
                            </tr>
                            <tr style={{ textWrap: "nowrap" }}>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>#</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Lesson</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Ver</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Qns</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Tuts</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>E</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>M</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>D</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Te</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Tm</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Td</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Tt</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Prcs</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Clues</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Sols</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>qB</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>qC</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>qD</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>qF</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>qI</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>qL</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>qM</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>qN</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>qO</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>qW</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>qZ</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Dic Description</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Lsn Description</th>
                                <th style={{ ...styles.maroonTh, position: 'sticky', top: 30, zIndex: 2 }}>Lesson2</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lessonsData.map((row, idx) => (
                                <tr
                                    key={idx}
                                    style={{
                                        ...(idx % 2 === 0 ? styles.defaultRow : styles.altRow),
                                        textWrap: "nowrap"
                                    }}
                                >
                                    <td style={styles.td}>{idx + 1}</td>
                                    <td style={styles.td}>{row.Lesson}</td>
                                    <td style={styles.td}>{row.Ver}</td>
                                    <td style={styles.td}>{row.Qns || ''}</td>
                                    <td style={styles.td}>{row.Tuts || ''}</td>
                                    <td style={styles.td}>{row.E || ''}</td>
                                    <td style={styles.td}>{row.M || ''}</td>
                                    <td style={styles.td}>{row.D || ''}</td>
                                    <td style={styles.td}>{row.Te || ''}</td>
                                    <td style={styles.td}>{row.Tm || ''}</td>
                                    <td style={styles.td}>{row.Td || ''}</td>
                                    <td style={styles.td}>{row.Tt || ''}</td>
                                    <td style={styles.td}>{row.Prcs || ''}</td>
                                    <td style={styles.td}>{row.Clues || ''}</td>
                                    <td style={styles.td}>{row.Sols || ''}</td>
                                    <td style={styles.td}>{row.qB || ''}</td>
                                    <td style={styles.td}>{row.qC || ''}</td>
                                    <td style={styles.td}>{row.qD || ''}</td>
                                    <td style={styles.td}>{row.qF || ''}</td>
                                    <td style={styles.td}>{row.qI || ''}</td>
                                    <td style={styles.td}>{row.qL || ''}</td>
                                    <td style={styles.td}>{row.qM || ''}</td>
                                    <td style={styles.td}>{row.qN || ''}</td>
                                    <td style={styles.td}>{row.qO || ''}</td>
                                    <td style={styles.td}>{row.qW || ''}</td>
                                    <td style={styles.td}>{row.qZ || ''}</td>
                                    <td style={styles.td}>{row.DicDescription}</td>
                                    <td style={styles.td}>{row.LsnDescription}</td>
                                    <td style={styles.td}>{row.Lesson2}</td>
                                </tr>
                            ))}
                            <tr style={{ backgroundColor: '#FFFFCC', fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>
                                <td style={styles.td}></td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>Totals</td>
                                <td style={styles.td}></td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Qns) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Tuts) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.E) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.M) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.D) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Te) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Tm) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Td) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Tt) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Prcs) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Clues) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.Sols) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qB) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qC) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qD) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qF) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qI) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qL) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qM) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qN) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qO) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qW) || 0), 0) || '0'}</td>
                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#0000CC', fontSize: "18px" }}>{lessonsData.reduce((s, r) => s + (Number(r.qZ) || 0), 0) || '0'}</td>
                                <td style={styles.td}></td>
                                <td style={styles.td}></td>
                                <td style={styles.td}></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            {/* </div> */}
        </div>
    );
};

export default EL_ManageDIC;